// A tiny, framework-free registry mapping a marker category (by normalized label) to its
// render style {shape, color}. The category (MarkerType) is the source of truth; a placed
// marker only stores its `label`, so AnomalyMarker resolves shape/colour from here at build
// time. Populate it from the org's marker types (setMarkerStyles) BEFORE markers are loaded.
//
// Kept as a plain module (not a Pinia store) so domain classes like AnomalyMarker can read it
// without a component/pinia context.

const stylesByLabel = new Map();
const stylesById = new Map();
const EMPTY = { shape: null, color: null };

// Must match the API's MarkerTypeViewModel.NormalizeLabel (trim, lowercase, strip spaces) so a
// marker's free-text label resolves to the same key as its category.
function normalize(label) {
  return (label || '').toString().trim().toLowerCase().replace(/\s+/g, '');
}

// Replace the registry from the org's marker types. Each type is expected to expose `id`
// (the category RowKey), `normalizedLabel` (or `label`), plus optional `shape` and `color`.
// We key by BOTH id and label: a marker resolves by its stored markerTypeId when present
// (stable), otherwise by label (legacy markers created before the id was stored).
export function setMarkerStyles(types) {
  stylesByLabel.clear();
  stylesById.clear();
  for (const type of types || []) {
    const style = { shape: type.shape || null, color: type.color || null };
    const labelKey = type.normalizedLabel || normalize(type.label);
    if (labelKey) stylesByLabel.set(labelKey, style);
    if (type.id) stylesById.set(type.id, style);
  }
}

// Look up the configured style by category id (markerTypeId). Returns nulls when unknown.
export function getMarkerStyleById(id) {
  return (id && stylesById.get(id)) || EMPTY;
}

// Look up the configured style by marker label. Returns nulls when the category has no
// configured style (callers fall back to a legacy/default style).
export function getMarkerStyle(label) {
  return stylesByLabel.get(normalize(label)) || EMPTY;
}

export function clearMarkerStyles() {
  stylesByLabel.clear();
  stylesById.clear();
}
